import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { IAppServiceLanguages } from '@app/app.interface';
import { AppService } from '@app/app.service';
import { AppHelper } from '@app/core/app.helper';
import { IButtonsPanelConfig } from '@components/buttons-panel/buttons-panel.interface';
import { IPanelToolsConfig } from '@components/panel-tools/panel-tools.interfaces';
import { stripHtml } from '../../helpers/text-helpers';
import { IDataSource } from './panel-title.interface';

/**
 * Компонент заголовка
 *
 * Родительский компонент может задать отступы для содержимого компонента заголовка,
 * но не для разделительных линий. Для этого нужно использовать класс add-side-padding:
 * <app-panel-title class="add-side-padding">
 *
 * Вертикальные отступы внутри заголовка 8 пикселей (согласно дизайну).
 * При необходимости их можно скукожить до 4. Для этого нужно использовать
 * <app-panel-title class="small-gaps">
 *
 * Текст заголовка имеет размер шрифта 16 пикселей и высоту строки 24 (согласно дизайну).
 * При необходимости их можно скукожить до 14 и 20. Для этого нужно использовать
 * <app-panel-title class="small-title">
 *
 * По умолчанию сверху над панелью есть отступ 8 пикселей. Его можно убрать так
 * <app-panel-title class="remove-padding-top">
 *
 * По умолчанию снизу под панелью есть отступ 16 пикселей. Его можно убрать так
 * <app-panel-title class="remove-margin-bottom">
 *
 * @package PanelTitle
 * @version 1.3
 * @author Dogaeva A.P.
 * @since 01.03.2023
 * @copyright (c) Internet-Frigate {@link http://www.ifrigate.ru}
 */
@Component({
    selector: 'app-panel-title',
    templateUrl: './panel-title.component.html',
    styleUrls: ['./panel-title.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelTitleComponent implements AfterViewInit, OnDestroy, OnChanges, OnInit {
    private readonly cdr = inject(ChangeDetectorRef);
    readonly appService = inject(AppService);

    /** Заголовок */
    @Input() title: string;

    /** Иконка для заголовка */
    @Input() titleIcon: string;

    /** Отображение нижней границы */
    @Input() @HostBinding('class.show-bottom-border') showBorder: boolean = true;

    /** Ссылка на страничку помощи */
    @Input() helpLink: string = null;

    /** Ссылка на источник данных */
    @Input() dataSource: IDataSource;

    /** Показывать ли полоску-разделитель внизу */
    @Input() separator: boolean = false;

    /** Конфигурация кнопок вспомогательной панели */
    @Input() buttonPanelConfig?: IButtonsPanelConfig;

    /** Конфиг панели инструментов */
    @Input() panelTools: IPanelToolsConfig;

    /** Данные о родителе */
    @Input() itemData: { [key: string]: any } = {};

    /** Включено ли массовое редактирование */
    @Input() isMassEditing: boolean;

    /** Набор id для массового редактирования  */
    @Input() massEditingRows: any;

    /** Это форма добавления нового элемента? */
    @Input() isNewItem: boolean;

    /** Показывать крестик? */
    @Input() showCloseButton: boolean = false;

    /** Цветовая метка заголовка */
    @Input() colorMarker: string;

    /** Должен ли заголовок раскрываться вообще или нет */
    @Input() shouldExpand: boolean = true;

    /** Отображать панель инструментов в одной строке с заголовком */
    @Input() showPanelToolsInTitle: boolean;

    /** События на кнопках в панели */
    @Output() panelClick = new EventEmitter<any>();

    /** Закрыть страницу */
    @Output() closePage = new EventEmitter<any>();

    /** События изменения языка формы */
    @Output() changeLang = new EventEmitter<IAppServiceLanguages>();

    /** События изменения высоты заголовка */
    @Output() heightChangeEvent = new EventEmitter<any>();

    /** Ссылка на элемент заголовка (сам заголовок) */
    @ViewChild('cardTitle') cardTitle: ElementRef;

    /** Ссылка на элемент заголовка (текст) */
    @ViewChild('cardTitleText') cardTitleText: ElementRef;

    /** Ссылка  на элемент заголовка (компонент) */
    @ViewChild('panelTitle') panelTitle: ElementRef;

    /** Раскрыт заголовок или нет */
    public titleCollapsed: boolean = true;

    /** Можно ли раскрыть заголовок? */
    public titleExpandable: boolean = false;

    /** Текущая ширина заголовка */
    public titleWidth: number;

    /** Текст подсказки */
    public tooltipText: string;

    /** ResizeObserver - смотрим на изменение заголовка */
    private observer: ResizeObserver;

    /** Есть ли в заголовке верхняя панель, или он выводится в одну линию */
    public isSingleRow: boolean = true;

    /** Высота элемента заголовка */
    public panelTitleHeight: number;

    /**
     * Установить язык дерева
     * @param lang объект языка
     */
    public setCurrentLangForm(lang: IAppServiceLanguages): void {
        this.changeLang.next(lang);
    }

    /**
     * Переключаем состояния заголовка
     */
    public toggleTitle(): void {
        if (this.titleExpandable && this.shouldExpand) {
            this.titleCollapsed = !this.titleCollapsed;
        }
    }

    /**
     * Вызов родительской функции
     * @param type тип
     * @param value значение
     * @param event событие нажатия
     */
    public callParent(type, value): void {
        this.panelClick.next({ name: type, data: value });
    }

    /**
     * Установка текущего состояния заголовка
     */
    private setTitleState() {
        if (this.cardTitleText?.nativeElement) {
            this.titleCollapsed = AppHelper.isEllipsisActive(this.cardTitleText.nativeElement);
        }

        this.titleExpandable = this.titleCollapsed;
    }

    /**
     * Получаем высоту заголовка и сохраняем в свойство
     */
    private getPanelTitleHeight() {
        if (this.panelTitle?.nativeElement) {
            this.panelTitleHeight = this.panelTitle.nativeElement.getBoundingClientRect().height;
            this.heightChangeEvent.emit(this.panelTitleHeight);
        }
    }

    ngOnInit(): void {
        if (this.title) {
            this.tooltipText = stripHtml(this.title);
        }

        // Если есть верх заголовка, включаем двустрочный режим отображения
        if (this.dataSource) {
            this.isSingleRow = false;
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.hasOwnProperty('title')) {
            this.tooltipText = stripHtml(changes?.title.currentValue);
        }
    }

    ngAfterViewInit(): void {
        this.setTitleState();
        this.cdr.detectChanges();
        this.getPanelTitleHeight();

        // Посчитаем изначальную ширину заголовка
        this.titleWidth = Math.round(this.cardTitle?.nativeElement.getBoundingClientRect().width);

        // Подписываемся на изменение размеров заголовка
        this.observer = new ResizeObserver((entries) => {
            const rectWidth = Math.round(entries[0].contentRect.width);
            if (this.titleWidth !== rectWidth) {
                this.titleWidth = rectWidth;

                // Перерисуем все заново
                this.titleCollapsed = true;
                this.cdr.detectChanges();
                this.setTitleState();
                this.cdr.detectChanges();
            }

            this.getPanelTitleHeight();
        });

        if (this.cardTitle?.nativeElement) {
            this.observer.observe(this.cardTitle.nativeElement);
        }
    }

    ngOnDestroy(): void {
        if (this.cardTitle?.nativeElement) {
            this.observer?.unobserve(this.cardTitle.nativeElement);
        }
    }
}
